/**
 * School Details Form Page - REFACTORED
 *
 * Modern implementation using:
 * - react-hook-form + Zod validation
 * - shadcn Form components
 * - Reusable SchoolAddressFieldset component
 * - Design system utilities from main.css
 *
 * Replaces: Old version with manual state management
 *
 * Features:
 * - Read-only fields (school name, SIIIR code, CUI)
 * - Editable fields (legal representative, phone, student count, addresses)
 * - Conditional delivery address
 * - Direct API submission (no data transformation)
 *
 * Refactored: 2025-11-02
 * Version: 2.0
 */

import { useSchoolDetailsWithForm } from '../hooks/useSchoolDetailsWithForm';
import { useFormNavigation, useConfirmationDialog } from '@/shared/hooks';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SchoolAddressFieldset } from '@/shared/components/forms/SchoolAddressFieldset';
import { Spinner } from '@/components/ui/spinner';
import { FaRotate } from 'react-icons/fa6';
import { AlertTriangle } from 'lucide-react';
import { saptamanaVerdeLipsa, sirLaData, dataLaSir } from '@/shared/validation/saptamana-verde';
import { DatePickerField } from '@/shared/components/forms/DatePickerField';

const ID_SECTIUNE_SAPTAMANA_VERDE = 'sectiune-saptamana-verde';

export default function SchoolDetailsFormPage() {
  const { form, onSubmit, loading, isDirty, schoolData, isError, refetch } = useSchoolDetailsWithForm();

  // Navigation blocking when form has unsaved changes
  const navigationDialog = useConfirmationDialog({
    onConfirm: () => blocker.proceed?.(),
    onCancel: () => blocker.reset?.(),
  });

  const { blocker } = useFormNavigation({
    isDirty,
    onNavigationAttempt: navigationDialog.show,
  });

  // Submission confirmation
  const submitDialog = useConfirmationDialog({
    onConfirm: async () => {
      console.log('[SchoolDetailsForm] Dialog confirmed');
      await onSubmit();
    },
  });

  // Watch for delivery address checkbox to show/hide delivery fields
  const sameAsSchool = form.watch('adresaLivrareAceeasiCuScoala');
  const [saptamanaVerdeStart, saptamanaVerdeNecunoscuta] = form.watch([
    'saptamanaVerdeStart',
    'saptamanaVerdeNecunoscuta',
  ]);

  return (
    <div className="flex-1 flex flex-col md:overflow-hidden">
      {/* `pt-10` sub `md` rezerva locul hamburgerului flotant din `AuthenticatedLayout`: la 360
          (emulare, 2026-09-16) h1 statea la y=65..81 si butonul la 20..64 -- 1 px liber, sub
          eroarea emulare/telefon (pe /profil 11 px). Acelasi fix ca formularul de comanda
          (bebbf37f), pe wrapper, nu pe card. De la `md` neschimbat. */}
      <div className="flex-1 pr-4 pt-10 ecotic-form-scrollbar md:overflow-y-auto md:pt-0">
        {/* Scrollerul e DOAR de la `md` in sus. Sub `md` formularul curge in
            pagina: un container derulabil pe telefon inseamna ca degetul misca
            altceva decat crede utilizatorul, si ascunde bara de actiuni sub el.
            Masurat la 360px: containerul chiar derula (scrollHeight > clientHeight). */}
        <div className="max-w-4xl mx-auto space-y-6 p-6 my-4">
          <Card className="w-full max-w-4xl">
        <CardHeader>
          <CardTitle as="h1">Actualizare Detalii Școală</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6" aria-busy={loading}>
          {/* Randul asta lipsea din toate cele 16 masuratori S1 (p2-4): formularul se
              randa IMEDIAT, cu campurile doar `disabled` cat timp se incarca, fara
              niciun indicator vizibil sau anuntat asistiv - "role=status/aria-busy = 0"
              nu era o componenta stricata, era absenta completa a oricarei anuntari.
              `<Spinner>` (ui/spinner.tsx) are deja `role="status"` incorporat -
              consumat aici, nu modificat. */}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-2 text-dark/70">
              <Spinner className="size-5" />
              <span>Se încarcă...</span>
            </div>
          )}

          {/* Un 500/retea la incarcarea datelor scolii NU mai randa tacut un formular
              gol, ca si cum scoala n-ar avea inca date (silent 500, precedent
              use-editions.ts:27-35) - se afiseaza eroarea, cu reincercare directa pe
              interogare (`refetch`), nu doar `window.location.reload()`. */}
          {isError ? (
            <Alert className="border-error bg-error/10">
              <AlertDescription>
                <div className="text-center">
                  <p className="mb-2 font-semibold text-error">Nu am putut încărca datele școlii</p>
                  <p className="text-error">Verificați conexiunea și încercați din nou.</p>
                  <Button type="button" variant="outline" className="mt-3" onClick={() => refetch()}>
                    <FaRotate className="mr-2" />
                    <span>Încearcă din nou</span>
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-6">
              {/* Avertisment: scoala n-are «Saptamana verde» desemnata (decizie owner 2026-09-15).
                  UN SINGUR avertisment, in capul formularului (directie S1) -- nu si langa
                  sectiune. Se citeste de pe scoala INCARCATA, deci dispare dupa o salvare reusita,
                  nu cand utilizatorul tasteaza. Butonul duce la sectiune: fara el, la 360 campurile
                  stau cu mult sub ecran. */}
              {saptamanaVerdeLipsa(schoolData) && (
                <Alert className="border-warning bg-warning/5">
                  <AlertTriangle className="text-warning" aria-hidden="true" />
                  <AlertDescription>
                    <p className="font-semibold text-warning">Săptămâna Verde nu este stabilită</p>
                    <p className="text-warning">
                      Alegeți intervalul în care școala organizează activitățile de mediu. Îl puteți
                      completa{' '}
                      <button
                        type="button"
                        className="inline-flex min-h-11 items-center font-semibold underline md:min-h-0"
                        onClick={() =>
                          document
                            .getElementById(ID_SECTIUNE_SAPTAMANA_VERDE)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }
                      >
                        mai jos
                      </button>
                      .
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* ===================================================================
                  READ-ONLY SECTION - School Identification
                  =================================================================== */}

              <div className="space-y-4">
                {/* School Name (Read-only) */}
                <div className="space-y-2">
                  {/* Fara FormField/FormItem (nu e camp RHF) - legatura eticheta/camp se face
                      manual, cu id/htmlFor, la fel ca la Checkbox-urile de mai jos. Fara asta,
                      `<FormLabel>` fara context genereaza un `<label>` fara `htmlFor`. */}
                  <div className="form-field-row">
                    <FormLabel htmlFor="numeInstitutieInvatamant" className="form-label-bold">Nume Instituție Învățământ:</FormLabel>
                    <div className="flex-1">
                      <Input
                        id="numeInstitutieInvatamant"
                        type="text"
                        name="numeInstitutieInvatamant"
                        value={schoolData?.numeInstitutieInvatamant || ''}
                        disabled
                        className="form-input-styled bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* SIIIR Code (Read-only) */}
                <div className="space-y-2">
                  <div className="form-field-row">
                    <FormLabel htmlFor="codSiiirUnitate" className="form-label-bold">Cod SIIIR:</FormLabel>
                    <div className="flex-1">
                      <Input
                        id="codSiiirUnitate"
                        type="text"
                        name="codSiiirUnitate"
                        value={schoolData?.codSiiirUnitate || ''}
                        disabled
                        className="form-input-styled bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* CUI (Read-only) */}
                <div className="space-y-2">
                  <div className="form-field-row">
                    <FormLabel htmlFor="cui" className="form-label-bold">CUI:</FormLabel>
                    <div className="flex-1">
                      <Input
                        id="cui"
                        type="text"
                        name="cui"
                        value={schoolData?.cui || ''}
                        disabled
                        className="form-input-styled bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Legal Representative (Editable) -- deblocat 2026-09-21: directorul se
                    schimba de la un an la altul, iar protocolul se completeaza din campul asta.
                    Acelasi tipar ca `telefon` (FormItem pe tot randul). */}
                <FormField
                  control={form.control}
                  name="numeReprezentatLegal"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <FormItem className="form-field-row">
                        <FormLabel className="form-label-bold">Reprezentant Legal: *</FormLabel>
                        <div className="flex-1 space-y-0">
                          <FormControl>
                            <Input
                              {...field}
                              type="text"
                              autoComplete="off"
                              disabled={loading}
                              aria-required="true"
                              className="form-input-styled"
                            />
                          </FormControl>
                          <FormDescription className="form-description-text">
                            Numele directorului, așa cum apare în protocol.
                          </FormDescription>
                          <FormMessage className="form-message-spacer" />
                        </div>
                      </FormItem>
                    </div>
                  )}
                />
              </div>

              {/* ===================================================================
                  EDITABLE SECTION - Contact Information
                  =================================================================== */}

              <div className="w-full rounded-md bg-gray-50 p-3">
                <div className="text-center text-lg font-bold text-gray-600">
                  Informații de Contact (Editabile)
                </div>
              </div>

              {/* Phone Number */}
              <FormField
                control={form.control}
                name="telefon"
                render={({ field }) => (
                  <div className="space-y-2">
                    {/* `FormItem` invelie tot randul (eticheta + camp), nu doar campul --
                        altfel `FormLabel` citeste un `FormItemContext` gol (e sora, nu fiica),
                        si `htmlFor` nu mai corespunde cu id-ul real (S1, tabelul p2, randul
                        p2-6; acelasi tipar ca #177 si constatarea 41). */}
                    <FormItem className="form-field-row">
                      <FormLabel className="form-label-bold">Telefon: *</FormLabel>
                      <div className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="text"
                            placeholder="0712345678"
                            disabled={loading}
                            aria-required="true"
                            className="form-input-styled"
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </div>
                    </FormItem>
                  </div>
                )}
              />

              {/* Number of Students */}
              <FormField
                control={form.control}
                name="numarElevi"
                render={({ field }) => (
                  <div className="space-y-2">
                    <FormItem className="form-field-row">
                      <FormLabel className="form-label-bold">Număr Elevi: *</FormLabel>
                      <div className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min="1"
                            disabled={loading}
                            aria-required="true"
                            className="form-input-styled"
                            onChange={(e) => field.onChange(
                              e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                            )}
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </div>
                    </FormItem>
                  </div>
                )}
              />

              {/* ===================================================================
                  SCHOOL ADDRESS SECTION
                  =================================================================== */}

              <div className="w-full rounded-md bg-gray-50 p-3">
                <div className="text-center text-lg font-bold text-gray-600">
                  Adresa Instituției de Învățământ
                </div>
              </div>

              {/* `ascundeDetaliiBloc`: o școală nu are apartament (decizia owner-ului din 18.09,
                  aceeasi prop ca pe `SchoolRequestFormPage`). Campurile raman in starea
                  formularului si se retrimit neatinse de `buildSchoolUpdatePayload` — ascunderea
                  nu goleste nimic. Pe ADRESA DE LIVRARE ele raman, deliberat: acolo poate fi un
                  bloc. */}
              <SchoolAddressFieldset
                namePrefix="adresa"
                disabled={loading}
                ascundeDetaliiBloc
              />

              {/* ===================================================================
                  DELIVERY ADDRESS SECTION
                  =================================================================== */}

              {/* Same as School Address Checkbox */}
              <FormField
                control={form.control}
                name="adresaLivrareAceeasiCuScoala"
                render={({ field }) => (
                  <div className="space-y-2">
                    <FormItem className="form-field-row items-center">
                      <FormLabel className="form-label-bold">
                        Adresa de livrare este aceeași cu adresa școlii?
                      </FormLabel>
                      <div className="flex-1 space-y-0">
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            {/* Switch, nu Checkbox (owner, 20.09). Eticheta de langa ramane:
                                poarta `min-h-11` (44px), adica exact tinta de atingere pe care
                                pista singura (`h-5 w-9`) nu o are — si e legata de control prin
                                `id`. Textul „Da"/„Nu" ramane si el: citeste starea explicit,
                                fara s-o lase pe seama nuantei pistei. */}
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={loading}
                              id="adresaLivrareAceeasiCuScoala"
                            />
                            <label
                              htmlFor="adresaLivrareAceeasiCuScoala"
                              className="flex min-h-11 items-center px-2 text-sm font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {field.value ? 'Da' : 'Nu'}
                            </label>
                          </div>
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </div>
                    </FormItem>
                  </div>
                )}
              />

              {/* Delivery Address (Conditional) */}
              {!sameAsSchool && (
                <>
                  <div className="w-full rounded-md bg-gray-50 p-3">
                    <div className="text-center text-lg font-bold text-gray-600">
                      Adresa de Livrare
                    </div>
                  </div>

                  <SchoolAddressFieldset
                    namePrefix="adresaLivrare"
                    disabled={loading}
                  />
                </>
              )}

              {/* ===================================================================
                  SAPTAMANA VERDE -- obligatorie SAU bifa (owner, 2026-09-16, acelasi tipar
                  ca SchoolRequestFormPage). `scroll-mt-20` sub md: tinta butonului din
                  avertisment nu are voie sa ajunga sub butonul fix al meniului (y 0-68 la 360).
                  =================================================================== */}

              <div
                id={ID_SECTIUNE_SAPTAMANA_VERDE}
                className="w-full scroll-mt-20 rounded-md bg-gray-50 p-3 md:scroll-mt-0"
              >
                <div className="text-center text-lg font-bold text-gray-600">Săptămâna Verde</div>
              </div>

              <FormField
                control={form.control}
                name="saptamanaVerdeNecunoscuta"
                render={({ field }) => (
                  <FormItem className="flex items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={loading}
                        id="saptamanaVerdeNecunoscuta"
                      />
                    </FormControl>
                    <FormLabel htmlFor="saptamanaVerdeNecunoscuta" className="text-sm font-normal cursor-pointer">
                      Nu cunosc încă intervalul, voi completa ulterior
                    </FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="saptamanaVerdeStart"
                render={({ field, fieldState }) => (
                  <DatePickerField
                    value={sirLaData(field.value)}
                    onChange={(data) => field.onChange(dataLaSir(data))}
                    label="Început:"
                    error={fieldState.error?.message}
                    disabled={loading || !!saptamanaVerdeNecunoscuta}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="saptamanaVerdeEnd"
                render={({ field, fieldState }) => (
                  <DatePickerField
                    value={sirLaData(field.value)}
                    onChange={(data) => field.onChange(dataLaSir(data))}
                    label="Sfârșit:"
                    minDate={sirLaData(saptamanaVerdeStart)}
                    error={fieldState.error?.message}
                    disabled={loading || !!saptamanaVerdeNecunoscuta}
                  />
                )}
              />
            </form>
          </Form>
          )}
        </CardContent>

        {/* ===================================================================
            ACTION BUTTONS
            =================================================================== */}
        {/* Butoanele au `w-full sm:w-auto` — dar randul nu se aseza NICIODATA pe
            coloana, deci doua butoane de latime 100% stateau unul langa altul
            intr-un rand de 294px: 246 + 16 + 246 = 508px. `justify-center`
            imparte depasirea in ambele parti, deci masurat la 360px pe build de
            productie: „Reincarcare" la left=-82 (in afara ecranului, la stanga,
            unde nu se poate derula) si butonul principal la right=426.
            Sub `sm` randul devine coloana; de la `sm` in sus, nimic nu se schimba. */}
        <CardFooter className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto"
            disabled={loading}
            onClick={() => window.location.reload()}
          >
            <FaRotate className="mr-2" />
            <span>Reîncărcare</span>
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto"
            disabled={!isDirty || loading || isError}
            onClick={submitDialog.show}
          >
            <span>{loading ? 'Se salvează...' : 'Actualizare'}</span>
          </Button>
        </CardFooter>
      </Card>

      {/* Navigation confirmation - shown when trying to leave page with unsaved changes */}
      <ConfirmationDialog
        isOpen={navigationDialog.isOpen}
        onClose={navigationDialog.handleCancel}
        onConfirm={navigationDialog.handleConfirm}
        title="Modificări nesalvate"
        description="Aveți modificări nesalvate. Doriți să părăsiți pagina?"
        confirmText="Părăsește pagina"
        cancelText="Rămâi pe pagină"
        variant="destructive"
      />

      {/* Submission confirmation - shown when clicking save button */}
      <ConfirmationDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.handleCancel}
        onConfirm={submitDialog.handleConfirm}
        title="Confirmare salvare"
        description="Doriți să salvați modificările?"
        confirmText="Salvează"
        cancelText="Anulare"
        loading={loading}
      />
        </div>
      </div>
    </div>
  );
}
